/**
 * Centralized Axios client with auth interceptor.
 * Replaces the scattered `axios + ls('auth_token')` pattern from the old codebase.
 */
import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { setupMockApi } from './mockApi';

/* ── Parse Server migration completed ──
 * Parse Server decommissioned. All endpoints return plain REST JSON.
 * Normalization helpers are no longer required.
 * ─────────────────────────────────────────────────────────────────────────── */

/**
 * Unwrap the Parse-style { result: T, error?: ... } response envelope.
 * - If response.data.error exists → throw (becomes an Axios rejection)
 * - If response.data.result is defined → unwrap to response.data.result
 * - Otherwise leave response.data as-is (already plain REST format)
 *
 * This makes every API method simpler: they use `data` directly, not `data.result`.
 * TODO: remove the unwrap once the backend drops the { result } envelope.
 */
function unwrapResult(data: unknown): unknown {
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const obj = data as Record<string, unknown>;
    // Error envelope → throw so Axios rejects the promise
    if (obj.error) {
      if (typeof obj.error === 'string') {
        if (obj.error === 'Unauthorized' || obj.error === 'Invalid token') {
          clearAuthToken();
          localStorage.removeItem('patient_me');
          window.location.hash = '#/login';
        }
        throw new Error(obj.error);
      }
      if (typeof obj.error === 'object') {
        const err = obj.error as Record<string, unknown>;
        const msg = (typeof err.message === 'string' ? err.message : null)
          ?? (typeof err.error === 'string' ? err.error : null)
          ?? 'API error';
        if (msg === 'Unauthorized' || msg === 'Invalid token') {
          clearAuthToken();
          localStorage.removeItem('patient_me');
          window.location.hash = '#/login';
        }
        throw new Error(msg);
      }
    }
    // Unwrap { result: T } envelope, but preserve paginated metadata objects containing 'total'
    if ('result' in obj && !('total' in obj)) return obj.result;
  }
  return data;
}

/* ── Axios client ── */

const API_ENDPOINT = (typeof import.meta !== 'undefined' && (import.meta as any)?.env?.VITE_API_ENDPOINT) || 'https://ep-test.langplat.com';

// Set to true to use mock data, false to use the real backend
const isMock = false;

// In dev mode use the Vite proxy (/api → real backend) to avoid CORS.
// In production, use the real API endpoint directly.
const baseURL = isMock
  ? API_ENDPOINT
  : (typeof import.meta !== 'undefined' && (import.meta as any)?.env?.DEV)
    ? '/api'
    : API_ENDPOINT;

const apiClient = axios.create({
  baseURL,
  timeout: isMock ? 500 : 90_000, // 90s — increased from 30s for slow connections (slides = ~1.85MB)
  headers: { 'Content-Type': 'application/json' },
});

if (isMock) {
  setupMockApi(apiClient);
}

/* ── Auth interceptor ── */

const TOKEN_KEY = 'auth_token';

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY) ||
         localStorage.getItem('sessionToken') ||
         localStorage.getItem('parse_session_token') ||
         localStorage.getItem('_session_token');
}

export function setAuthToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearAuthToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

// Auth token prefix: '' for Parse session tokens, 'Bearer ' for JWT.
// TODO: set VITE_AUTH_TOKEN_PREFIX=Bearer  once the backend issues JWTs.
const AUTH_PREFIX = (typeof import.meta !== 'undefined' && (import.meta as any)?.env?.VITE_AUTH_TOKEN_PREFIX) || '';

// Parse App ID — needed by legacy Parse-based backend handlers.
const PARSE_APP_ID = (typeof import.meta !== 'undefined' && (import.meta as any)?.env?.VITE_PARSE_APP_ID) || '';

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAuthToken();
    if (token) {
      const value = AUTH_PREFIX + token;
      if (config.headers && typeof config.headers.set === 'function') {
        config.headers.set('Authorization', value);
        // Also send as Parse session token — legacy Parse-based handlers check this header.
        // TODO: remove once all backend endpoints use JWT/Authorization instead.
        config.headers.set('X-Parse-Session-Token', token);
      } else {
        config.headers.Authorization = value;
        (config.headers as any)['X-Parse-Session-Token'] = token;
      }
    }
    if (PARSE_APP_ID) {
      if (config.headers && typeof config.headers.set === 'function') {
        config.headers.set('X-Parse-Application-Id', PARSE_APP_ID);
      } else {
        (config.headers as any)['X-Parse-Application-Id'] = PARSE_APP_ID;
      }
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

/* ── Response interceptors ── */

apiClient.interceptors.response.use(
  (response) => {
    try {
      // Unwrap Parse { result: T } envelope and surface Parse errors as rejections
      response.data = unwrapResult(response.data);
    } catch (err) {
      // Re-throw errors from unwrapResult (Parse API errors) as proper rejections
      return Promise.reject(err);
    }
    return response;
  },
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      clearAuthToken();
      localStorage.removeItem('patient_me');
      // Redirect to login when token is invalid / expired
      window.location.hash = '#/login';
    }
    return Promise.reject(error);
  },
);

export default apiClient;
